import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Download, Share, Smartphone } from 'lucide-react';
import { checkInstallable, isPWA } from '../serviceWorkerRegistration';

const InstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [installAction, setInstallAction] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already installed as PWA
    if (isPWA()) {
      return;
    }

    // Check if user has dismissed recently
    const dismissedAt = localStorage.getItem('pwa_dismissed');
    if (dismissedAt) {
      const daysSinceDismiss = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismiss < 7) {
        return;
      }
    }

    // Detect iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Check for installability
    checkInstallable((canInstall, installFn) => {
      if (canInstall) {
        setInstallAction(() => installFn);
        setTimeout(() => setShowPrompt(true), 3000); // Show after 3 seconds
      } else if (isIOSDevice) {
        setTimeout(() => setShowPrompt(true), 3000);
      }
    });
  }, []);

  const handleInstall = () => {
    if (installAction) {
      installAction();
    }
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setDismissed(true);
    setShowPrompt(false);
    localStorage.setItem('pwa_dismissed', Date.now().toString());
  };

  if (!showPrompt || dismissed) return null;

  return (
    <div className="fixed bottom-20 md:bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-slideIn">
      <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-dark-slate rounded-xl flex items-center justify-center mr-3">
              <Smartphone className="w-6 h-6 text-mint-fresh" />
            </div>
            <div>
              <h3 className="font-semibold text-dark-slate">Install GradLink</h3>
              <p className="text-sm text-slate-gray">Add to your home screen</p>
            </div>
          </div>
          <button 
            onClick={handleDismiss}
            className="text-slate-gray hover:text-dark-slate"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {isIOS ? (
          <div className="bg-slate-50 rounded-xl p-3 mb-3">
            <p className="text-sm text-slate-gray">
              Tap <Share className="w-4 h-4 inline mx-1" /> then "Add to Home Screen"
            </p>
          </div>
        ) : (
          <p className="text-sm text-slate-gray mb-3">
            Get quick access, offline support, and push notifications
          </p>
        )}
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex-1 rounded-full"
            onClick={handleDismiss}
          >
            Maybe Later
          </Button>
          {!isIOS && (
            <Button
              className="flex-1 rounded-full bg-dark-slate"
              onClick={handleInstall}
              data-testid="install-pwa"
            >
              <Download className="w-4 h-4 mr-2" />
              Install
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
